import type {
  DragMatchTask,
  DragSortTask,
  PageRef,
  ReviewState,
  SingleChoiceTask,
  Task,
} from "./schema";

const optionIds = ["A", "B", "C", "D"] as const;

interface ChoiceSeed {
  order: number;
  sceneId: string;
  title: string;
  stem: string;
  options: readonly string[];
  correctIndex: number;
  hint: string;
  explanation: string;
  feedbacks?: readonly string[];
  pageRefs?: PageRef[];
  reviewState?: ReviewState;
  presentation?: "cards" | "select";
}

function makeChoice(seed: ChoiceSeed): SingleChoiceTask {
  const correctOptionId = optionIds[seed.correctIndex];
  if (!correctOptionId) {
    throw new Error(`Task ${seed.order} has an invalid correctIndex.`);
  }

  return {
    id: `T${String(seed.order).padStart(2, "0")}`,
    order: seed.order,
    sceneId: seed.sceneId,
    title: seed.title,
    type: "single_choice",
    presentation: seed.presentation ?? "cards",
    stem: seed.stem,
    options: seed.options.map((text, index) => ({
      id: optionIds[index] ?? String(index + 1),
      text,
      feedback:
        seed.feedbacks?.[index] ??
        (index === seed.correctIndex
          ? `判断正确：${seed.explanation}`
          : `这不是本题所问的关键史实。${seed.hint}`),
      misconceptionCode:
        index === seed.correctIndex ? undefined : `T${seed.order}_OPTION_${index + 1}`,
    })),
    correctOptionId,
    hint: seed.hint,
    explanation: seed.explanation,
    pageRefs: seed.pageRefs ?? [],
    reviewState: seed.reviewState ?? "seed",
    formal: true,
  };
}

const task04: DragSortTask = {
  id: "T04",
  order: 4,
  sceneId: "M1S2",
  title: "抗日战争三阶段排序",
  type: "drag_sort",
  stem: "请按正确顺序排列抗日战争的三个阶段。",
  items: [
    { id: "defense", text: "战略防御" },
    { id: "counteroffensive", text: "战略反攻" },
    { id: "stalemate", text: "战略相持" },
  ],
  correctOrder: ["defense", "stalemate", "counteroffensive"],
  hint: "观察双方力量对比从劣势、平衡到优势的变化。",
  explanation: "《论持久战》科学预见抗日战争将依次经过战略防御、战略相持、战略反攻三个阶段。",
  pageRefs: [{ print: "76—77", pdf: "96—97" }],
  reviewState: "approved",
  formal: true,
};

const task12: DragMatchTask = {
  id: "T12",
  order: 12,
  sceneId: "M4S1",
  title: "敌后歼敌方法配对",
  type: "drag_match",
  stem: "请将以下歼敌方法与正确描述配对。",
  left: [
    { id: "sparrow", text: "麻雀战" },
    { id: "tunnel", text: "地道战" },
    { id: "mine", text: "地雷战" },
    { id: "sabotage", text: "破袭战" },
  ],
  right: [
    { id: "scatter", text: "分散袭扰，像麻雀一样东打一枪西打一枪" },
    { id: "underground", text: "利用地下坑道隐蔽出击" },
    { id: "homemade", text: "利用自制地雷杀伤敌人" },
    { id: "transport", text: "破坏敌人交通线和据点" },
  ],
  correctPairs: {
    sparrow: "scatter",
    tunnel: "underground",
    mine: "homemade",
    sabotage: "transport",
  },
  hint: "从作战空间、武器与主要目标三个角度辨认。",
  explanation: "敌后军民因地制宜发展了多样化的人民战争战法。",
  pageRefs: [{ print: "92—93", pdf: "112—113" }],
  reviewState: "approved",
  formal: true,
};

const task17: DragMatchTask = {
  id: "T17",
  order: 17,
  sceneId: "M5S3",
  title: "整风运动三大内容配对",
  type: "drag_match",
  stem: "请将整风运动的三项任务与正确内容配对。",
  left: [
    { id: "subjectivism", text: "反对主观主义" },
    { id: "sectarianism", text: "反对宗派主义" },
    { id: "stereotyped", text: "反对党八股" },
  ],
  right: [
    { id: "study", text: "整顿学风" },
    { id: "party", text: "整顿党风" },
    { id: "writing", text: "整顿文风" },
  ],
  correctPairs: {
    subjectivism: "study",
    sectarianism: "party",
    stereotyped: "writing",
  },
  hint: "分别判断问题影响的是学习方法、党内关系还是表达方式。",
  explanation: "整风运动以反对主观主义、宗派主义和党八股分别整顿学风、党风和文风。",
  pageRefs: [{ print: "101", pdf: "121" }],
  reviewState: "approved",
  formal: true,
};

const task19: DragSortTask = {
  id: "T19",
  order: 19,
  sceneId: "M6S3",
  title: "抗战胜利关键事件排序",
  type: "drag_sort",
  stem: "请按时间顺序排列以下历史事件。",
  items: [
    { id: "final_battle", text: "8月9日·毛泽东发表《对日寇的最后一战》" },
    { id: "rescript", text: "8月15日·日本天皇发布《终战诏书》" },
    { id: "surrender", text: "9月2日·日本代表在投降书上签字" },
    { id: "taiwan", text: "10月25日·中国政府在台湾举行受降仪式" },
  ],
  correctOrder: ["final_battle", "rescript", "surrender", "taiwan"],
  hint: "先识别8月的全面反攻与停战节点，再排列9月和10月的受降节点。",
  explanation: "四个节点依次呈现全面反攻、宣布终战、签署投降书和台湾受降的历史进程。",
  pageRefs: [{ print: "108", pdf: "128" }],
  reviewState: "approved",
  formal: true,
};

export const tasks: Task[] = [
  makeChoice({
    order: 1,
    sceneId: "M1S1",
    title: "国共合作宣言与统一战线形成",
    stem: "1937年9月，什么事件的发表宣告了国共两党重新合作和抗日民族统一战线形成？",
    options: [
      "七七事变爆发",
      "中共中央《为公布国共合作宣言》和蒋介石谈话的发表",
      "红军改编为八路军",
      "洛川会议召开",
    ],
    correctIndex: 1,
    hint: "回看9月22日和23日的两份公开文件，区分“抗战爆发”“军队改编”和“合作正式形成”。",
    explanation:
      "9月22日中共中央宣言和23日蒋介石谈话的发表，宣告国共两党重新合作和抗日民族统一战线形成。",
    feedbacks: [
      "七七事变标志全民族抗战爆发，但尚不是两党重新合作正式形成的标志。",
      "判断正确。两份文件连续公开，使合作由推动过程进入正式形成阶段。",
      "军队改编是合作进程中的重要制度性表现，但题目问的是“宣告形成”的公开事件。",
      "洛川会议形成党的全面抗战路线，属于下一个场景的核心知识。",
    ],
    pageRefs: [{ print: "74", pdf: "94" }],
    reviewState: "approved",
  }),
  makeChoice({
    order: 2,
    sceneId: "M1S2",
    title: "洛川会议确定的作战方针",
    stem: "洛川会议确定红军的基本作战方针是：",
    options: [
      "集中主力打大规模运动战",
      "独立自主的山地游击战争",
      "配合国民党军队打阵地战",
      "分散到各地发动武装暴动",
    ],
    correctIndex: 1,
    hint: "关注“敌人后方”“独立自主”“山地”三个关键词。",
    explanation: "洛川会议强调在敌人后方放手发动独立自主的山地游击战争。",
    pageRefs: [{ print: "75—76", pdf: "95—96" }],
    reviewState: "approved",
  }),
  makeChoice({
    order: 3,
    sceneId: "M1S2",
    title: "《论持久战》",
    stem: "毛泽东分析中日战争双方有四个基本特点。以下哪项不属于这四个特点？",
    options: [
      "日本是帝国主义强国，中国是半殖民地半封建弱国",
      "日本的侵略战争是退步的，中国的反侵略战争是进步的",
      "美国将会出兵援助中国",
      "日本是小国经不起长期战争，中国是大国能支持长期战争",
    ],
    correctIndex: 2,
    hint: "四个特点都来自中日双方国力、战争性质、国土规模与国际道义的比较。",
    explanation: "“美国将会出兵援助中国”不属于《论持久战》概括的四个基本特点。",
    pageRefs: [{ print: "76", pdf: "96" }],
    reviewState: "approved",
  }),
  task04,
  makeChoice({
    order: 5,
    sceneId: "M2S1",
    title: "平型关大捷的历史意义",
    stem: "平型关大捷的历史意义是：",
    options: [
      "彻底消灭了日军第5师团",
      "全民族抗战爆发后中国军队主动对日作战取得的第一个重大胜利",
      "标志着抗日战争转入战略反攻",
      "直接导致了日本侵略者投降",
    ],
    correctIndex: 1,
    hint: "辨认“第一个重大胜利”的准确范围，避免夸大直接结果。",
    explanation: "平型关大捷是全民族抗战爆发后中国军队主动对日作战取得的第一个重大胜利。",
    pageRefs: [{ print: "79—80", pdf: "99—100" }],
    reviewState: "approved",
  }),
  makeChoice({
    order: 6,
    sceneId: "M2S2",
    title: "敌后由中共领导建立的第一个统一战线性质的抗日民主政权",
    stem: "1938年1月10日，敌后由中国共产党领导建立的第一个统一战线性质的抗日民主政权是：",
    options: ["陕甘宁边区政府", "晋察冀边区临时行政委员会", "晋冀鲁豫边区政府", "山东抗日民主政府"],
    correctIndex: 1,
    hint: "回看晋察冀边区1938年1月10日的政权建设档案。",
    explanation: "晋察冀边区临时行政委员会是敌后第一个统一战线性质的抗日民主政权。",
    pageRefs: [{ print: "81", pdf: "101" }],
    reviewState: "approved",
  }),
  makeChoice({
    order: 7,
    sceneId: "M2S3",
    title: "六届六中全会的理论贡献",
    stem: "六届六中全会首次提出了一个重大命题，它标志着中国共产党在理论上的成熟：",
    options: ["新民主主义革命", "马克思主义中国化", "抗日民族统一战线", "党的建设伟大工程"],
    correctIndex: 1,
    hint: "回看六届六中全会首次提出的重大理论命题。",
    explanation: "六届六中全会首次提出了马克思主义中国化的重大命题。",
    pageRefs: [{ print: "83", pdf: "103" }],
    reviewState: "approved",
  }),
  makeChoice({
    order: 8,
    sceneId: "M3S1",
    title: "《为抗战两周年纪念对时局宣言》三大口号",
    stem: "1939年7月7日，党中央发出《为抗战两周年纪念对时局宣言》。以下哪句属于其三大口号？",
    options: [
      "停止内战，一致抗日",
      "巩固国内团结——反对内部分裂！",
      "打倒蒋介石，解放全中国",
      "打过长江去，解放全中国",
    ],
    correctIndex: 1,
    hint: "三大口号围绕坚持抗战、巩固团结和力求进步。",
    explanation: "“巩固国内团结——反对内部分裂！”属于三大口号之一。",
    pageRefs: [{ print: "84", pdf: "104" }],
    reviewState: "approved",
  }),
  makeChoice({
    order: 9,
    sceneId: "M3S2",
    title: "百团大战参战部队数量",
    stem: "百团大战参战部队达多少个团、20余万人？",
    options: ["95个团", "105个团", "115个团", "125个团"],
    correctIndex: 1,
    hint: "原方案中的正式答案为105个团。",
    explanation: "百团大战参战部队达105个团、20余万人。",
    presentation: "select",
    pageRefs: [{ print: "87", pdf: "107" }],
    reviewState: "approved",
  }),
  makeChoice({
    order: 10,
    sceneId: "M3S4",
    title: "重建新四军军部",
    stem: "皖南事变后重建新四军军部，新任代军长和政治委员分别是：",
    options: ["叶挺、项英", "陈毅、刘少奇", "朱德、彭德怀", "林彪、聂荣臻"],
    correctIndex: 1,
    hint: "重建后的新四军军部任命发生在皖南事变之后。",
    explanation: "陈毅任新四军代军长，刘少奇任政治委员。",
    pageRefs: [{ print: "90", pdf: "110" }],
    reviewState: "approved",
  }),
  makeChoice({
    order: 11,
    sceneId: "M4S1",
    title: "潘家峪惨案",
    stem: "1941年1月下旬，日军在冀东制造了屠杀群众约1300人的惨案，这是：",
    options: ["南京大屠杀", "潘家峪惨案", "平顶山惨案", "厂窖惨案"],
    correctIndex: 1,
    hint: "地点线索是冀东，时间为1941年1月下旬。",
    explanation: "该惨案是潘家峪惨案；相关内容必须采用庄重的史料见证式呈现。",
    pageRefs: [{ print: "92", pdf: "112" }],
    reviewState: "approved",
  }),
  task12,
  makeChoice({
    order: 13,
    sceneId: "M4S2",
    title: "《为人民服务》讲演",
    stem: "1944年9月，毛泽东在张思德追悼会上发表的著名讲演是：",
    options: ["《纪念白求恩》", "《为人民服务》", "《愚公移山》", "《反对自由主义》"],
    correctIndex: 1,
    hint: "该讲演题名直接概括了队伍的根本宗旨。",
    explanation: "毛泽东在张思德追悼会上发表了《为人民服务》的讲演。",
    pageRefs: [{ print: "93—94", pdf: "113—114" }],
    reviewState: "approved",
  }),
  makeChoice({
    order: 14,
    sceneId: "M4S3",
    title: "联合政府主张",
    stem: "1944年9月，林伯渠根据党中央指示，在国民参政会上正式提出：",
    options: [
      "打倒蒋介石，解放全中国",
      "立即结束国民党一党统治、建立各抗日党派民主联合政府",
      "废止国民党一党专政，实行土地革命",
      "召开政治协商会议，成立民主联合政府",
    ],
    correctIndex: 1,
    hint: "主张同时包含结束一党统治与建立各抗日党派联合政府。",
    explanation: "林伯渠正式提出立即结束国民党一党统治、建立各抗日党派民主联合政府。",
    pageRefs: [{ print: "97", pdf: "117" }],
    reviewState: "approved",
  }),
  makeChoice({
    order: 15,
    sceneId: "M5S1",
    title: "三大法宝",
    stem: "毛泽东在《〈共产党人〉发刊词》中提出，中国共产党战胜敌人的三个主要法宝是：",
    options: [
      "理论联系实际、密切联系群众、批评与自我批评",
      "统一战线、武装斗争、党的建设",
      "党的领导、人民军队、统一战线",
      "武装斗争、土地革命、根据地建设",
    ],
    correctIndex: 1,
    hint: "区分“三大法宝”与党的“三大作风”。",
    explanation: "统一战线、武装斗争、党的建设是中国革命中战胜敌人的三个主要法宝。",
    pageRefs: [{ print: "98—99", pdf: "118—119" }],
    reviewState: "approved",
  }),
  makeChoice({
    order: 16,
    sceneId: "M5S3",
    title: "整风运动的最主要任务",
    stem: "整风运动的最主要任务是：",
    options: [
      "反对宗派主义以整顿党风",
      "反对主观主义以整顿学风",
      "反对党八股以整顿文风",
      "反对自由主义以整顿纪律",
    ],
    correctIndex: 1,
    hint: "最主要任务聚焦思想路线和学习方法。",
    explanation: "整风运动最主要的任务是反对主观主义以整顿学风。",
    pageRefs: [{ print: "101", pdf: "121" }],
    reviewState: "approved",
  }),
  task17,
  makeChoice({
    order: 18,
    sceneId: "M6S1",
    title: "七大政治路线",
    stem: "党的七大提出的政治路线是：",
    options: [
      "打倒日本帝国主义，建立新中国",
      "放手发动群众，壮大人民力量，在我党的领导下，打败日本侵略者，解放全国人民，建立一个新民主主义的中国",
      "废止国民党一党专政，建立民主联合政府",
      "实现社会主义工业化",
    ],
    correctIndex: 1,
    hint: "完整表述包含发动群众、壮大人民力量、党的领导和建立新民主主义中国。",
    explanation: "第二项完整呈现了七大提出的政治路线。",
    pageRefs: [{ print: "103—104", pdf: "123—124" }],
    reviewState: "approved",
  }),
  task19,
];
